import { useState, useRef } from "react";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";

const services = [
  "Digital Publishing",
  "Accessibility",
  "Content Management",
  "XML Conversion",
  "Typesetting",
  "Graphics Studio",
  "Interactive Learning",
  "Technology Solutions",
  "Other",
];

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-[14px] text-white placeholder:text-white/50 outline-none focus:border-[#00B8A9] transition-colors";
const labelCls = "block mb-1.5 text-[13px] font-semibold text-white/90";

const Req = () => (
  <>
    <span aria-hidden="true">*</span>
    <span className="sr-only">(required)</span>
  </>
);

export const ContactForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const summaryRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.target);
    const newErrors = {};
    if (!data.get("name").trim()) newErrors.name = "Please enter your full name.";
    const email = data.get("email").trim();
    if (!email) newErrors.email = "Please enter your business email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Please enter a valid email address, for example name@company.com.";
    if (!data.get("message").trim()) newErrors.message = "Please tell us briefly about your project.";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => summaryRef.current?.focus(), 50);
      return;
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div data-testid="contact-form-success" role="status" aria-live="polite" className="relative flex flex-col items-center justify-center text-center py-16">
        <CheckCircle2 className="h-14 w-14 text-[#00B8A9]" aria-hidden="true" />
        <h4 className="mt-5 font-display text-2xl font-bold tracking-tight">Thank you!</h4>
        <p className="mt-2 text-white/80 text-[15px] max-w-xs">
          Your message has been received. Our team will get back to you within one business day.
        </p>
      </div>
    );
  }

  const errorKeys = Object.keys(errors);

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} noValidate className="relative space-y-4" aria-label="Contact form">
      {errorKeys.length > 0 && (
        <div
          data-testid="contact-form-error-summary"
          ref={summaryRef}
          tabIndex={-1}
          role="alert"
          className="rounded-xl border border-red-300/50 bg-red-500/15 p-4"
        >
          <div className="flex items-center gap-2 font-semibold text-[14px] text-red-100">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            There {errorKeys.length === 1 ? "is 1 problem" : `are ${errorKeys.length} problems`} with the form
          </div>
          <ul className="mt-2 ml-6 list-disc space-y-1 text-[13px] text-red-100">
            {errorKeys.map((k) => (
              <li key={k}>
                <a href={`#cf-${k}`} className="underline">{errors[k]}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cf-name" className={labelCls}>Full Name <Req /></label>
          <input
            data-testid="contact-form-name"
            id="cf-name"
            name="name"
            type="text"
            required
            autoComplete="name"
            aria-invalid={errors.name ? "true" : undefined}
            aria-describedby={errors.name ? "cf-name-error" : undefined}
            className={inputCls}
          />
          {errors.name && (
            <p id="cf-name-error" className="mt-1 text-[12px] text-red-200">{errors.name}</p>
          )}
        </div>
        <div>
          <label htmlFor="cf-organization" className={labelCls}>Organization</label>
          <input data-testid="contact-form-organization" id="cf-organization" name="organization" type="text" autoComplete="organization" className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cf-jobtitle" className={labelCls}>Job Title</label>
          <input data-testid="contact-form-jobtitle" id="cf-jobtitle" name="jobtitle" type="text" autoComplete="organization-title" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>Business Email <Req /></label>
          <input
            data-testid="contact-form-email"
            id="cf-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            aria-invalid={errors.email ? "true" : undefined}
            aria-describedby={errors.email ? "cf-email-error" : undefined}
            className={inputCls}
          />
          {errors.email && (
            <p id="cf-email-error" className="mt-1 text-[12px] text-red-200">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cf-phone" className={labelCls}>Phone Number</label>
          <input data-testid="contact-form-phone" id="cf-phone" name="phone" type="tel" autoComplete="tel" className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-country" className={labelCls}>Country</label>
          <input data-testid="contact-form-country" id="cf-country" name="country" type="text" autoComplete="country-name" className={inputCls} />
        </div>
      </div>

      <div>
        <label htmlFor="cf-service" className={labelCls}>Service Interested In</label>
        <select data-testid="contact-form-service" id="cf-service" name="service" className={`${inputCls} [&>option]:text-[#0B1F3A]`} defaultValue="">
          <option value="">Select a service</option>
          {services.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="cf-message" className={labelCls}>Project Details <Req /></label>
        <textarea
          data-testid="contact-form-message"
          id="cf-message"
          name="message"
          required
          rows={4}
          placeholder="Tell us about your project"
          aria-invalid={errors.message ? "true" : undefined}
          aria-describedby={errors.message ? "cf-message-error" : undefined}
          className={inputCls}
        />
        {errors.message && (
          <p id="cf-message-error" className="mt-1 text-[12px] text-red-200">{errors.message}</p>
        )}
      </div>

      <button
        data-testid="contact-form-submit"
        type="submit"
        className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0B1F3A] px-6 py-3.5 text-[15px] font-semibold hover:bg-[#00B8A9] hover:text-white transition-colors"
      >
        Send Message
        <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </button>
    </form>
  );
};
